import { usePlayground } from "@/components/Playground2/playground-store";
import { useAIPromptAssisstant } from "@/queries/playground/useAIPromptAssisstant";
import { SparklesIcon } from "@heroicons/react/outline";
import { SparklesIcon as SparklesIcon2 } from "@heroicons/react/solid";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import LoadingSpinner from "../LoadingSpinner";
import {
  DiffViewerVariant,
  RawBlueprintDiffViewer,
} from "../ui/CommitMessageDialog/BlueprintDiffViewer";
import { BasicTooltip } from "../ui/Tooltip";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";
import { Agent } from "../icons";

const loadingPhrases = [
  "Taming your prompt...",
  "Wrangling those wild ideas...",
  "Saddling up the AI...",
  "Riding the neural trails...",
  "Corralling the possibilities...",
  "Rounding up the best response...",
  "Breaking in new concepts...",
  "Hitching up the algorithm...",
  "Blazing the digital frontier...",
  "Herding the neural networks...",
  "Rustling up some inspiration...",
  "Moseying through the data...",
  "Drawing from the idea well...",
  "Spinning yarns of code...",
];

export const AIPromptAssistant = () => {
  const playground = usePlayground();
  const [prompt, setPrompt] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isDiffDialogOpen, setIsDiffDialogOpen] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);

  const {
    mutate: generatePrompt,
    isLoading,
    data: generatedPrompt,
  } = useAIPromptAssisstant();

  const handleGeneratePrompt = useCallback(() => {
    if (!prompt.trim()) return;

    playground.setIsAIPromptAssistantLoading(true);
    generatePrompt(
      { prompt, blueprint: playground.blueprint },
      {
        onSuccess: () => {
          playground.setIsAIPromptAssistantLoading(false);
          setPrompt("");
        },
        onError: () => {
          playground.setIsAIPromptAssistantLoading(false);
        },
      },
    );
  }, [generatePrompt, playground, prompt]);

  useEffect(() => {
    if (!isOpen && !isLoading) {
      setPrompt("");
    }
  }, [isOpen, isLoading]);

  useEffect(() => {
    if (generatedPrompt) {
      setIsDiffDialogOpen(true);
    }
  }, [generatedPrompt]);

  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % loadingPhrases.length);
      }, 2500);

      return () => clearInterval(interval);
    }
  }, [isLoading]);

  const handleApplyPrompt = useCallback(() => {
    if (!generatedPrompt) return;

    playground.applyPromptBlueprintToState(generatedPrompt);
    setIsDiffDialogOpen(false);
  }, [generatedPrompt, playground]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!prompt.trim()) {
          setIsShaking(true);
          return;
        }
        handleGeneratePrompt();
      }
    },
    [handleGeneratePrompt, prompt],
  );

  const handleDiscard = useCallback(() => {
    setIsDiffDialogOpen(false);
    setPrompt("");
    setIsOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        handleDiscard();
      }
    },
    [handleDiscard],
  );

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger>
          <BasicTooltip
            content={isLoading ? loadingPhrases[0] : "AI Prompt Assistant"}
          >
            <Button variant="ghost" size="icon">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isLoading ? "loading" : "idle"}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  className="flex"
                >
                  {isLoading ? (
                    <SparklesIcon2 className="duration-750 h-5 w-5 animate-pulse text-blue-500 transition-all" />
                  ) : (
                    <SparklesIcon className="h-4 w-4" />
                  )}
                </motion.span>
              </AnimatePresence>
            </Button>
          </BasicTooltip>
        </PopoverTrigger>
        <PopoverContent className="w-[350px]" align="center">
          <span className="flex items-center gap-2 text-sm font-medium">
            AI Prompt Assistant
          </span>
          <motion.div
            className="relative w-full"
            animate={isShaking ? { x: [0, -8, 8, -8, 8, 0] } : { x: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onAnimationComplete={() => setIsShaking(false)}
          >
            <Textarea
              disabled={isLoading}
              onKeyDown={handleKeyDown}
              placeholder="Create a prompt that writes a poem from a desired language on a specific topic"
              className="mt-2 min-h-[100px]"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </motion.div>
          <BasicTooltip shouldShow={!prompt} content="Enter a prompt to begin">
            <span className="text-sm font-medium">
              <Button
                variant="outline"
                disabled={isLoading || !prompt.trim()}
                className="group relative mt-2 w-full !opacity-100"
                onClick={handleGeneratePrompt}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 items-center overflow-hidden">
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={currentPhraseIndex}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -20, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="block"
                        >
                          {loadingPhrases[currentPhraseIndex]}
                        </motion.span>
                      </AnimatePresence>
                      <motion.div
                        layout
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="ml-2"
                      >
                        <LoadingSpinner size={4} />
                      </motion.div>
                    </div>
                  </div>
                ) : (
                  <span className="flex items-center gap-2">
                    Update
                    <span className="select-none opacity-60 transition-opacity duration-200 ease-in-out group-hover:opacity-85">
                      ↩
                    </span>
                  </span>
                )}
              </Button>
            </span>
          </BasicTooltip>
        </PopoverContent>
      </Popover>
      <Dialog open={isDiffDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[800px] max-w-[75vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              PromptLayer AI Assistant <Agent className="h-4 w-4" />
            </DialogTitle>
          </DialogHeader>
          {generatedPrompt && (
            <RawBlueprintDiffViewer
              className="max-h-[600px]"
              variant={DiffViewerVariant.SPLIT}
              oldBlueprint={playground.blueprint}
              newBlueprint={generatedPrompt}
            />
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleDiscard}>
              Discard
            </Button>
            <Button onClick={handleApplyPrompt}>Apply</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
